import { useState, useCallback, useRef, useEffect } from 'react';
import type { DeviceInfo } from '@/types/api';
import type { ToastMessage } from './Toast';
import { cropApi } from '@/services/cropApi';
import CaptureModal from './CaptureModal';

interface DeviceTableProps {
  devices: DeviceInfo[];
  loading: boolean;
  error: string;
  onRefresh: () => void;
  onToast: (toast: ToastMessage) => void;
}

const CAPTURE_TIMEOUT_MS = 3 * 60 * 1000; // 3분
const POLL_INTERVAL_MS = 5000; // 5초

export default function DeviceTable({
  devices,
  loading,
  error,
  onRefresh,
  onToast,
}: DeviceTableProps) {
  const [capturingSet, setCapturingSet] = useState<Set<string>>(new Set());
  const [modalDevice, setModalDevice] = useState<DeviceInfo | null>(null);
  const pollTimersRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());
  const timeoutTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // 컴포넌트 언마운트 시 모든 타이머 정리
  useEffect(() => {
    return () => {
      pollTimersRef.current.forEach((timer) => clearInterval(timer));
      timeoutTimersRef.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  const cleanupTimers = useCallback((deviceId: string) => {
    const pollTimer = pollTimersRef.current.get(deviceId);
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimersRef.current.delete(deviceId);
    }
    const timeoutTimer = timeoutTimersRef.current.get(deviceId);
    if (timeoutTimer) {
      clearTimeout(timeoutTimer);
      timeoutTimersRef.current.delete(deviceId);
    }
  }, []);

  const startCapturePolling = useCallback(
    (deviceId: string, deviceName: string) => {
      // 상태 폴링 시작
      let pollCount = 0;
      const maxPolls = Math.ceil(CAPTURE_TIMEOUT_MS / POLL_INTERVAL_MS);

      const pollTimer = setInterval(async () => {
        pollCount++;
        try {
          const res = await cropApi.getCaptureStatus(deviceId);

          if (res.status === 'done') {
            cleanupTimers(deviceId);
            setCapturingSet((prev) => {
              const next = new Set(prev);
              next.delete(deviceId);
              return next;
            });
            const timeStr = res.capture_time
              ? res.capture_time.substring(11, 19)
              : '';
            onToast({
              id: Date.now(),
              text: `촬영 완료 (${timeStr})`,
              type: 'success',
            });
            // 목록 갱신하여 last_capture 업데이트
            setTimeout(() => onRefresh(), 500);
            return;
          }

          if (res.status === 'fail') {
            cleanupTimers(deviceId);
            setCapturingSet((prev) => {
              const next = new Set(prev);
              next.delete(deviceId);
              return next;
            });
            onToast({
              id: Date.now(),
              text: '촬영 실패',
              type: 'error',
            });
            return;
          }

          // 타임아웃 체크
          if (pollCount >= maxPolls) {
            cleanupTimers(deviceId);
            setCapturingSet((prev) => {
              const next = new Set(prev);
              next.delete(deviceId);
              return next;
            });
            onToast({
              id: Date.now(),
              text: '촬영 결과를 확인할 수 없습니다. 장비 상태를 확인하세요.',
              type: 'warning',
            });
          }
        } catch {
          // 폴링 실패 시 무시하고 계속
          if (pollCount >= maxPolls) {
            cleanupTimers(deviceId);
            setCapturingSet((prev) => {
              const next = new Set(prev);
              next.delete(deviceId);
              return next;
            });
            onToast({
              id: Date.now(),
              text: '촬영 결과를 확인할 수 없습니다. 장비 상태를 확인하세요.',
              type: 'warning',
            });
          }
        }
      }, POLL_INTERVAL_MS);

      pollTimersRef.current.set(deviceId, pollTimer);

      // 3분 타임아웃
      const timeoutTimer = setTimeout(() => {
        cleanupTimers(deviceId);
        setCapturingSet((prev) => {
          const next = new Set(prev);
          next.delete(deviceId);
          return next;
        });
        onToast({
          id: Date.now(),
          text: '촬영 결과를 확인할 수 없습니다. 장비 상태를 확인하세요.',
          type: 'warning',
        });
      }, CAPTURE_TIMEOUT_MS);

      timeoutTimersRef.current.set(deviceId, timeoutTimer);
    },
    [cleanupTimers, onRefresh, onToast]
  );

  const handleCaptureClick = useCallback((device: DeviceInfo) => {
    if (!device.online) return;
    if (capturingSet.has(device.device_id)) return;
    setModalDevice(device);
  }, [capturingSet]);

  const handleCaptureConfirm = useCallback(async () => {
    if (!modalDevice) return;
    const device = modalDevice;
    setModalDevice(null);

    const deviceId = device.device_id;
    const deviceName = device.name;

    // 촬영 중 상태로 설정
    setCapturingSet((prev) => {
      const next = new Set(prev);
      next.add(deviceId);
      return next;
    });

    try {
      const res = await cropApi.sendCapture(deviceId);
      if (res.result === 'success') {
        onToast({
          id: Date.now(),
          text: '촬영 명령을 전송했습니다. 완료까지 약 1분 소요됩니다.',
          type: 'info',
        });
        // 폴링 시작
        startCapturePolling(deviceId, deviceName);
      } else {
        // 실패 시 즉시 복구
        setCapturingSet((prev) => {
          const next = new Set(prev);
          next.delete(deviceId);
          return next;
        });
        onToast({
          id: Date.now(),
          text: res.message || '촬영 명령 전송 실패',
          type: 'error',
        });
      }
    } catch {
      setCapturingSet((prev) => {
        const next = new Set(prev);
        next.delete(deviceId);
        return next;
      });
      onToast({
        id: Date.now(),
        text: '서버 연결 실패',
        type: 'error',
      });
    }
  }, [modalDevice, onToast, startCapturePolling]);

  const handleCaptureCancel = useCallback(() => {
    setModalDevice(null);
  }, []);

  const formatLastCapture = (lastCapture: string) => {
    if (!lastCapture || lastCapture === '0000-00-00 00:00:00') return '-';
    return lastCapture;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
        <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
          <i className="ri-loader-4-line text-4xl animate-spin" style={{ color: '#27ae60' }}></i>
        </div>
        <p className="text-gray-500 text-sm">장비 목록을 불러오는 중...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
        <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
          <i className="ri-error-warning-line text-4xl" style={{ color: '#e74c3c' }}></i>
        </div>
        <p className="text-gray-600 text-sm mb-4">{error}</p>
        <button
          onClick={onRefresh}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white cursor-pointer whitespace-nowrap transition-all hover:brightness-110"
          style={{ backgroundColor: '#27ae60' }}
        >
          다시 시도
        </button>
      </div>
    );
  }

  if (devices.length === 0) {
    return (
      <div className="bg-white rounded-2xl border border-gray-100 p-10 text-center">
        <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
          <i className="ri-cpu-line text-4xl text-gray-300"></i>
        </div>
        <p className="text-gray-500 text-sm">등록된 장비가 없습니다.</p>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50">
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                장비명
              </th>
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Device ID
              </th>
              <th className="text-center px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                온라인 상태
              </th>
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                마지막 촬영
              </th>
              <th className="text-center px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                촬영
              </th>
            </tr>
          </thead>
          <tbody>
            {devices.map((device, idx) => {
              const isCapturing = capturingSet.has(device.device_id);
              const isOnline = device.online;

              return (
                <tr
                  key={device.device_id}
                  className={`border-b border-gray-50 transition-colors hover:bg-gray-50/50 ${
                    idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                  }`}
                >
                  <td className="px-5 py-4">
                    <span className="text-sm font-semibold text-gray-800">
                      {device.name}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <code className="text-xs bg-gray-100 px-2 py-1 rounded-md text-gray-600 font-mono">
                      {device.device_id}
                    </code>
                  </td>
                  <td className="px-5 py-4 text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <span
                        className={`w-2 h-2 rounded-full shrink-0 ${
                          isOnline ? 'bg-green-500' : 'bg-gray-300'
                        }`}
                      ></span>
                      <span
                        className={`text-xs font-medium ${
                          isOnline ? 'text-green-600' : 'text-gray-400'
                        }`}
                      >
                        {isOnline ? '온라인' : '오프라인'}
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className="text-sm text-gray-600">
                      {formatLastCapture(device.last_capture)}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-center">
                    {isCapturing ? (
                      <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-lg bg-gray-100">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i
                            className="ri-loader-4-line text-sm animate-spin"
                            style={{ color: '#27ae60' }}
                          ></i>
                        </div>
                        <span className="text-xs font-medium text-gray-500 whitespace-nowrap">
                          촬영 중...
                        </span>
                      </div>
                    ) : (
                      <div className="relative inline-block">
                        <button
                          onClick={() => handleCaptureClick(device)}
                          disabled={!isOnline}
                          className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold cursor-pointer whitespace-nowrap transition-all ${
                            isOnline
                              ? 'text-white hover:brightness-110'
                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                          style={
                            isOnline
                              ? { backgroundColor: '#27ae60' }
                              : undefined
                          }
                          title={
                            !isOnline ? '장비가 오프라인입니다' : undefined
                          }
                        >
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i
                              className={`${
                                isOnline
                                  ? 'ri-camera-line text-white'
                                  : 'ri-camera-line text-gray-400'
                              }`}
                            ></i>
                          </div>
                          촬영
                        </button>
                        {!isOnline && (
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none">
                            장비가 오프라인입니다
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-3">
        {devices.map((device) => {
          const isCapturing = capturingSet.has(device.device_id);
          const isOnline = device.online;

          return (
            <div
              key={device.device_id}
              className="bg-white rounded-xl border border-gray-100 p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-bold text-gray-800">
                  {device.name}
                </span>
                <div className="flex items-center gap-1.5">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      isOnline ? 'bg-green-500' : 'bg-gray-300'
                    }`}
                  ></span>
                  <span
                    className={`text-xs font-medium ${
                      isOnline ? 'text-green-600' : 'text-gray-400'
                    }`}
                  >
                    {isOnline ? '온라인' : '오프라인'}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <code className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-500 font-mono">
                  {device.device_id}
                </code>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  마지막 촬영: {formatLastCapture(device.last_capture)}
                </span>
                {isCapturing ? (
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i
                        className="ri-loader-4-line text-xs animate-spin"
                        style={{ color: '#27ae60' }}
                      ></i>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      촬영 중...
                    </span>
                  </div>
                ) : (
                  <button
                    onClick={() => handleCaptureClick(device)}
                    disabled={!isOnline}
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-all ${
                      isOnline
                        ? 'text-white hover:brightness-110'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                    style={isOnline ? { backgroundColor: '#27ae60' } : undefined}
                  >
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-camera-line"></i>
                    </div>
                    촬영
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Capture Confirmation Modal */}
      {modalDevice && (
        <CaptureModal
          deviceName={modalDevice.name}
          deviceId={modalDevice.device_id}
          onCancel={handleCaptureCancel}
          onConfirm={handleCaptureConfirm}
        />
      )}
    </>
  );
}